/**
 * Page publique pour consulter (et signer) un document envoyé par l'ETA.
 * URL: /doc/{token}
 * Pas de login requis - le token est la preuve d'autorisation.
 */
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CheckCircle2,
  Loader2,
  FileText,
  ShieldCheck,
  Clock,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LABELS = {
  devis: 'Devis',
  contrat: 'Contrat',
  avenant: 'Avenant',
  bon_livraison: 'Bon de livraison',
  facture: 'Facture',
};

export default function DocumentPubliquePage() {
  const { token } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [signerNom, setSignerNom] = useState('');
  const [signing, setSigning] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/eta-notifications/doc/${token}`)
      .then((r) => setDoc(r.data))
      .catch((e) =>
        setError(e?.response?.data?.detail || 'Document introuvable')
      )
      .finally(() => setLoading(false));
  }, [token]);

  const handleSign = async () => {
    if (!signerNom.trim()) {
      toast.error('Saisissez votre nom complet pour signer');
      return;
    }
    setSigning(true);
    try {
      const r = await axios.post(`${API}/eta-notifications/doc/${token}/signer`, {
        signed_by: signerNom.trim(),
        accept: true,
      });
      toast.success('Document signé avec succès');
      setDoc({ ...doc, signed_at: r.data.signed_at, signed_by: signerNom });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la signature');
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (error || !doc) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="text-5xl mb-4">🔒</div>
          <h1 className="text-xl font-bold text-slate-800">Document introuvable</h1>
          <p className="text-sm text-slate-500 mt-2">
            {error || 'Ce lien est peut-être expiré.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen py-10"
      style={{
        background:
          'linear-gradient(135deg, #0f172a 0%, #134e4a 40%, #365314 100%)',
      }}
      data-testid="document-publique-page"
    >
      <div className="max-w-3xl mx-auto px-6">
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          <div className="p-8 bg-gradient-to-br from-emerald-800 to-lime-600 text-white">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-5 h-5" />
              <span className="text-sm uppercase tracking-wider opacity-80">
                {LABELS[doc.type_document] || 'Document'}
              </span>
            </div>
            <h1 className="text-3xl font-bold">{doc.titre}</h1>
            {doc.montant_ttc != null && (
              <p className="mt-4 text-xl opacity-95">
                Montant total TTC :{' '}
                <strong>{doc.montant_ttc.toLocaleString('fr-FR')} €</strong>
              </p>
            )}
          </div>

          <div className="p-8 space-y-6">
            <div>
              <div className="text-xs text-slate-500">Destinataire</div>
              <div className="font-medium text-slate-900">{doc.client_nom}</div>
            </div>

            {doc.resume && (
              <div>
                <div className="text-xs text-slate-500 mb-1">Résumé</div>
                <p className="text-slate-700 whitespace-pre-line leading-relaxed">
                  {doc.resume}
                </p>
              </div>
            )}

            {doc.pdf_base64 && (
              <div>
                <a
                  href={`data:application/pdf;base64,${doc.pdf_base64}`}
                  download={`${doc.titre}.pdf`}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-800 text-sm font-medium"
                  data-testid="document-download-pdf"
                >
                  <FileText className="w-4 h-4" />
                  Télécharger le PDF
                </a>
              </div>
            )}

            <div className="flex items-center gap-2 text-sm text-slate-500 pt-4 border-t border-slate-200">
              <Clock className="w-4 h-4" />
              Reçu le {new Date(doc.created_at).toLocaleString('fr-FR')}
            </div>

            {doc.signed_at ? (
              <div className="p-5 rounded-2xl bg-emerald-50 border border-emerald-200 flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-emerald-900">
                    Document signé
                  </div>
                  <div className="text-sm text-emerald-700 mt-1">
                    Par <strong>{doc.signed_by}</strong> le{' '}
                    {new Date(doc.signed_at).toLocaleString('fr-FR')}
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-5 rounded-2xl bg-amber-50 border border-amber-200">
                <div className="flex items-center gap-2 mb-3">
                  <ShieldCheck className="w-5 h-5 text-amber-700" />
                  <div className="font-semibold text-amber-900">
                    Accuser réception et signer
                  </div>
                </div>
                <p className="text-sm text-amber-800 mb-4">
                  En signant, vous reconnaissez avoir consulté ce {LABELS[doc.type_document]?.toLowerCase()} et
                  en accepter les termes. L'horodatage et votre adresse IP
                  seront enregistrés comme preuve juridique.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    data-testid="document-signer-name"
                    value={signerNom}
                    onChange={(e) => setSignerNom(e.target.value)}
                    placeholder="Votre nom et prénom"
                    className="flex-1 px-4 py-2.5 rounded-lg border border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                  <button
                    data-testid="document-sign-btn"
                    onClick={handleSign}
                    disabled={signing || !signerNom.trim()}
                    className="px-5 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {signing ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4" />
                    )}
                    Signer
                  </button>
                </div>
              </div>
            )}

            <p className="text-xs text-slate-400 text-center">
              Document émis via {window.location.host} — Ce lien est unique et horodaté.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
